import React from 'react'
import { Monitor, Moon, Sun } from 'lucide-react'
import { useTheme } from '../context/ThemeContext'

export default function DashboardPreview() {
  const { isDark, theme } = useTheme()

  return (
    <section className={`py-20 transition-colors duration-300 ${
      isDark
        ? 'bg-gradient-to-br from-gray-900 to-gray-800'
        : 'bg-gradient-to-br from-gray-50 to-gray-100'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className={`inline-block px-4 py-2 rounded-full mb-4 transition-colors duration-300 ${
            isDark
              ? 'bg-gradient-to-r from-cyan-900/30 to-blue-900/30'
              : 'bg-gradient-to-r from-cyan-100 to-blue-100'
          }`}>
            <span className={`font-semibold text-sm transition-colors duration-300 ${
              isDark ? 'text-cyan-400' : 'text-cyan-700'
            }`}>
              <Monitor className="w-4 h-4 inline mr-2" />
              FULL DASHBOARD
            </span>
          </div>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-4 transition-colors duration-300 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Powerful Dashboard,
            <span className="gradient-text"> Simple to Use</span>
          </h2>
          <p className={`text-lg sm:text-xl max-w-2xl mx-auto transition-colors duration-300 ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Everything you need at a glance. Track revenue, orders, customers, and more.
          </p>
        </div>

        {/* Theme Status */}
        <div className="flex justify-center mb-8">
          <div className={`inline-flex items-center rounded-xl p-1 shadow-lg border transition-colors duration-300 ${
            isDark
              ? 'bg-gray-800 border-gray-700'
              : 'bg-white border-gray-200'
          }`}>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
              isDark
                ? 'bg-cyan-500 text-white shadow-md'
                : 'bg-cyan-50 text-cyan-700'
            }`}>
              {isDark ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              <span className="text-sm font-medium">Matches site {theme} mode</span>
            </div>
          </div>
        </div>

        {/* Dashboard Screenshot */}
        <div className="relative max-w-5xl mx-auto">
          <div className={`rounded-t-xl p-3 flex items-center gap-2 transition-colors duration-300 ${
            isDark ? 'bg-gray-700' : 'bg-gray-200'
          }`}>
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
            </div>
            <div className="flex-1 mx-4">
              <div className={`rounded-lg px-4 py-1.5 text-sm flex items-center gap-2 transition-colors duration-300 ${
                isDark ? 'bg-gray-600 text-gray-300' : 'bg-white text-gray-500'
              }`}>
                <span className="text-green-500">🔒</span>
                app.laundrymate.shop
              </div>
            </div>
          </div>

          <div className={`relative rounded-b-xl overflow-hidden shadow-2xl border-x border-b transition-colors duration-300 ${
            isDark ? 'border-gray-600' : 'border-gray-300'
          }`}>
            <img
              src={isDark ? '/dashboard-dark.png' : '/dashboard-light.png'}
              alt={`LaundryMate Dashboard - ${isDark ? 'Dark' : 'Light'} Mode`}
              className="w-full h-auto transition-all duration-300"
            />

            <div className={`absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t to-transparent pointer-events-none transition-colors duration-300 ${
              isDark ? 'from-gray-900' : 'from-gray-50'
            }`} />
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            {[
              { title: 'Revenue Tracking', desc: 'See daily, weekly, monthly sales at a glance' },
              { title: 'Order Status', desc: 'Track pending, washing, ready orders instantly' },
              { title: 'Sales Analytics', desc: 'Know your best services and peak days' }
            ].map((item, index) => (
              <div key={index} className={`rounded-xl p-4 shadow-lg border text-center transition-colors duration-300 ${
                isDark
                  ? 'bg-gray-800 border-gray-700'
                  : 'bg-white border-gray-100'
              }`}>
                <h4 className={`font-bold mb-1 transition-colors duration-300 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>{item.title}</h4>
                <p className={`text-sm transition-colors duration-300 ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-12">
          <a
            href="https://app.laundrymate.shop"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
          >
            Get Started Now
          </a>
        </div>
      </div>
    </section>
  )
}
